/**
 * German: the default and the source of truth for keys. Every other locale must
 * have exactly the same shape (enforced by the `Messages` type in index.ts).
 * Interpolation uses i18next syntax: {{name}}.
 */
export const de = {
  app: {
    name: 'Holymoly',
    tagline: 'Marketing-Plattform der Bechtle PLM'
  },
  common: {
    loading: 'Wird geladen …',
    retry: 'Erneut versuchen',
    close: 'Schließen',
    reset: 'Zurücksetzen',
    unknownError: 'Ein unerwarteter Fehler ist aufgetreten.'
  },
  nav: {
    sectionGeneral: 'Allgemein',
    overview: 'Übersicht',
    account: 'Mein Konto',
    linkedin: 'LinkedIn',
    campaigns: 'Kampagnen',
    leads: 'Leads',
    crm: 'CRM',
    crmQueue: 'Übertragungen',
    crmMapping: 'Feldzuordnung',
    dashboards: 'Dashboards',
    datasets: 'Datenquellen'
  },
  layout: {
    toggleSidebar: 'Seitenleiste ein-/ausblenden',
    toggleSidebarHover: 'Seitenleiste kompakt',
    toggleTheme: 'Farbschema wechseln',
    openCustomizer: 'Darstellung anpassen',
    language: 'Sprache',
    footer: 'Bechtle PLM · Marketing'
  },
  customizer: {
    title: 'Darstellung',
    fullscreen: 'Vollbild',
    theme: 'Farbschema',
    themes: {
      light: 'Hell',
      contrast: 'Kontrast',
      material: 'Material',
      dark: 'Dunkel',
      dim: 'Gedimmt',
      'material-dark': 'Material dunkel',
      system: 'System'
    },
    sidebar: 'Seitenleiste',
    sidebarOnlyLight: '(nur bei den Farbschemata Hell und Kontrast)',
    sidebarLight: 'Hell',
    sidebarDark: 'Dunkel'
  },
  auth: {
    signIn: 'Anmelden',
    signInTitle: 'Bei Holymoly anmelden',
    signInLead: 'Mit deiner E-Mail-Adresse und deinem Passwort.',
    email: 'E-Mail-Adresse',
    password: 'Passwort',
    showPassword: 'Passwort anzeigen',
    forgotPassword: 'Passwort vergessen?',
    passkey: 'Mit Passkey anmelden',
    or: 'oder',
    invalidCredentials: 'E-Mail-Adresse oder Passwort ist falsch.',
    tooManyAttempts: 'Zu viele Versuche. Bitte warte einen Moment.',
    passkeyFailed: 'Anmeldung mit Passkey hat nicht geklappt.',
    twoFactorTitle: 'Bestätigungscode',
    twoFactorLead: 'Gib den 6-stelligen Code aus deiner Authenticator-App ein.',
    code: 'Code',
    verify: 'Bestätigen',
    useBackupCode: 'Stattdessen einen Wiederherstellungscode verwenden',
    useTotp: 'Stattdessen Code aus der App verwenden',
    backupCode: 'Wiederherstellungscode',
    invalidCode: 'Der Code stimmt nicht. Bitte versuche es erneut.',
    trustDevice: 'Diesem Gerät 30 Tage vertrauen',
    signOut: 'Abmelden',
    backToSignIn: 'Zurück zur Anmeldung',
    forgotTitle: 'Passwort zurücksetzen',
    forgotLead: 'Wir schicken dir einen Link, mit dem du ein neues Passwort festlegst.',
    sendLink: 'Link senden',
    forgotSent: 'Falls es ein Konto mit dieser Adresse gibt, ist der Link unterwegs. Er ist eine Stunde gültig.',
    resetTitle: 'Neues Passwort festlegen',
    newPassword: 'Neues Passwort',
    confirmPassword: 'Passwort wiederholen',
    passwordHint: 'Mindestens 12 Zeichen. Ein Satz aus mehreren Wörtern ist leicht zu merken und sicher.',
    passwordsDiffer: 'Die Passwörter stimmen nicht überein.',
    passwordTooShort: 'Das Passwort muss mindestens 12 Zeichen lang sein.',
    resetDone: 'Dein Passwort wurde geändert. Du kannst dich jetzt anmelden.',
    resetInvalid: 'Dieser Link ist ungültig oder abgelaufen. Fordere bitte einen neuen an.',
    savePassword: 'Passwort speichern',
    inviteTitle: 'Willkommen bei Holymoly',
    inviteLead: 'Du wurdest als {{email}} eingeladen. Lege deinen Namen und dein Passwort fest.',
    inviteInvalid: 'Diese Einladung ist ungültig, abgelaufen oder wurde schon verwendet. Bitte lass dir eine neue schicken.',
    yourName: 'Dein Name',
    createAccount: 'Konto anlegen',
    sessionExpired: 'Deine Sitzung ist abgelaufen. Bitte melde dich erneut an.'
  },
  account: {
    title: 'Mein Konto',
    tabProfile: 'Profil',
    tabSecurity: 'Sicherheit',
    tabTokens: 'API-Tokens',
    name: 'Name',
    language: 'Sprache der Oberfläche',
    save: 'Speichern',
    saved: 'Gespeichert.',
    changePassword: 'Passwort ändern',
    currentPassword: 'Aktuelles Passwort',
    passwordChanged: 'Passwort geändert. Andere Sitzungen wurden abgemeldet.',
    twoFactor: 'Zwei-Faktor-Authentifizierung',
    twoFactorRequired: 'Für deine Rolle ist Zwei-Faktor-Authentifizierung vorgeschrieben. Richte sie jetzt ein, um Holymoly zu nutzen.',
    twoFactorOn: 'Aktiv',
    twoFactorOff: 'Nicht eingerichtet',
    enableTwoFactor: 'Einrichten',
    disableTwoFactor: 'Deaktivieren',
    twoFactorScan: 'Scanne den QR-Code mit deiner Authenticator-App (z. B. Microsoft Authenticator).',
    twoFactorQrAlt: 'QR-Code für die Authenticator-App',
    twoFactorStep1: 'Kein Scan möglich? Wähle in der App „Schlüssel manuell eingeben“ und übernimm diesen Schlüssel:',
    twoFactorOpenApp: 'Auf dem Smartphone direkt in der App öffnen',
    twoFactorStep2: 'Gib dann den 6-stelligen Code aus der App ein:',
    secretKey: 'Schlüssel',
    copy: 'Kopieren',
    copied: 'Kopiert',
    backupCodes: 'Wiederherstellungscodes',
    backupCodesLead: 'Bewahre diese Codes sicher auf. Jeder funktioniert einmal, falls du keinen Zugriff auf deine App hast.',
    confirmWithPassword: 'Zur Bestätigung dein Passwort:',
    twoFactorEnabled: 'Zwei-Faktor-Authentifizierung ist eingerichtet.',
    twoFactorDisabled: 'Zwei-Faktor-Authentifizierung wurde deaktiviert.',
    cannotDisableRequired: 'Deine Rolle schreibt Zwei-Faktor-Authentifizierung vor; sie kann nicht deaktiviert werden.',
    passkeys: 'Passkeys',
    passkeysLead: 'Melde dich ohne Passwort an – mit Windows Hello, Fingerabdruck oder Sicherheitsschlüssel.',
    addPasskey: 'Passkey hinzufügen',
    passkeyName: 'Bezeichnung (z. B. „Laptop“)',
    noPasskeys: 'Noch keine Passkeys.',
    remove: 'Entfernen',
    tokensLead: 'Persönliche Tokens für Skripte und KI-Assistenten (REST-API und MCP). Ein Token kann höchstens, was du selbst darfst.',
    tokenName: 'Bezeichnung',
    tokenScope: 'Berechtigungen',
    tokenExpires: 'Gültig für',
    days: '{{count}} Tage',
    createToken: 'Token erstellen',
    tokenCreated: 'Dein neues Token – es wird nur jetzt angezeigt:',
    noTokens: 'Noch keine Tokens.',
    revoke: 'Widerrufen',
    revoked: 'Widerrufen',
    expired: 'Abgelaufen',
    lastUsed: 'Zuletzt benutzt',
    never: 'nie',
    expiresAt: 'Läuft ab',
    noPermissionForTokens: 'Deine Rolle erlaubt keine API-Tokens.'
  },
  admin: {
    section: 'Verwaltung',
    users: 'Benutzer',
    roles: 'Rollen',
    audit: 'Protokoll',
    invite: 'Einladen',
    inviteTitle: 'Benutzer einladen',
    inviteSent: 'Einladung an {{email}} verschickt.',
    inviteLinkManual: 'Mailversand ist aus. Schicke diesen Link selbst (7 Tage gültig):',
    inviteLanguage: 'Sprache der Einladung',
    openInvites: 'Offene Einladungen',
    resend: 'Neu senden',
    resent: 'Neuer Link verschickt; der alte gilt nicht mehr.',
    revokeInvite: 'Zurückziehen',
    expiresOn: 'gültig bis {{date}}',
    inviteExpired: 'abgelaufen',
    name: 'Name',
    email: 'E-Mail',
    rolesCol: 'Rollen',
    status: 'Status',
    lastSeen: 'Zuletzt aktiv',
    active: 'Aktiv',
    deactivated: 'Deaktiviert',
    twoFactorShort: '2FA',
    editRoles: 'Rollen bearbeiten',
    deactivate: 'Deaktivieren',
    reactivate: 'Reaktivieren',
    resetTwoFactor: '2FA zurücksetzen',
    confirmDeactivate: '{{name}} deaktivieren? Alle Sitzungen und API-Tokens werden sofort beendet.',
    confirmResetTwoFactor: '2FA von {{name}} zurücksetzen? Die Person muss sie bei der nächsten Anmeldung neu einrichten.',
    noRoles: 'keine',
    cancel: 'Abbrechen',
    confirm: 'Bestätigen',
    newRole: 'Neue Rolle',
    editRole: 'Rolle bearbeiten',
    roleName: 'Name',
    roleDescription: 'Beschreibung',
    members: '{{count}} Mitglieder',
    system: 'System',
    requireTwoFactor: '2FA vorschreiben',
    requireTwoFactorForced: 'Erforderlich, weil die Rolle sensible Berechtigungen enthält.',
    sensitive: 'sensibel',
    allPermissions: 'Alle Berechtigungen',
    deleteRole: 'Löschen',
    confirmDeleteRole: 'Rolle „{{name}}“ löschen?',
    auditAction: 'Aktion',
    auditActor: 'Von',
    auditTarget: 'Ziel',
    auditTime: 'Zeit',
    auditDetails: 'Details',
    loadMore: 'Mehr laden',
    filterAction: 'Nach Aktion filtern',
    all: 'Alle',
    system_actor: 'System'
  },
  permissionModules: {
    admin: 'Verwaltung',
    account: 'Konto',
    linkedin: 'LinkedIn',
    leads: 'Leads',
    crm: 'CRM',
    dashboards: 'Dashboards'
  },
  permissions: {
    'users.view': 'Benutzer und Einladungen sehen',
    'users.manage': 'Benutzer einladen, Rollen zuweisen, deaktivieren',
    'roles.manage': 'Rollen anlegen und bearbeiten',
    'audit.view': 'Protokoll einsehen',
    'settings.manage': 'Systemeinstellungen ändern',
    'api_tokens.create': 'Eigene API-Tokens erstellen',
    'linkedin.view': 'LinkedIn-Konten, Kampagnen und Statistiken sehen',
    'linkedin.connect': 'LinkedIn-Verbindung herstellen und trennen',
    'linkedin.webhooks.manage': 'LinkedIn-Webhooks verwalten',
    'leads.view': 'Leads sehen (ohne Kontaktdaten)',
    'leads.view_pii': 'Kontaktdaten von Leads sehen (E-Mail, Telefon)',
    'leads.export': 'Leads exportieren',
    'leads.erase': 'Personendaten löschen (DSGVO)',
    'crm.view': 'CRM-Synchronisation sehen',
    'crm.mapping.manage': 'CRM-Feldzuordnung bearbeiten',
    'crm.sync.release': 'Zurückgehaltene Leads ins CRM freigeben',
    'crm.sync.retry': 'Fehlgeschlagene CRM-Übertragungen wiederholen',
    'dashboards.view': 'Dashboards ansehen',
    'dashboards.edit': 'Dashboards bearbeiten',
    'dashboards.publish': 'Dashboards veröffentlichen',
    'dashboards.sql': 'Eigene SQL-Abfragen in Dashboards',
    'datasets.manage': 'Datensätze und Kennzahlen verwalten'
  },
  auditActions: {
    'invite.created': 'Einladung erstellt',
    'invite.resent': 'Einladung neu gesendet',
    'invite.revoked': 'Einladung zurückgezogen',
    'invite.accepted': 'Einladung angenommen',
    'user.roles_changed': 'Rollen geändert',
    'user.deactivated': 'Benutzer deaktiviert',
    'user.reactivated': 'Benutzer reaktiviert',
    'user.two_factor_reset': '2FA zurückgesetzt',
    'role.created': 'Rolle erstellt',
    'role.updated': 'Rolle geändert',
    'role.deleted': 'Rolle gelöscht',
    'api_token.created': 'API-Token erstellt',
    'api_token.revoked': 'API-Token widerrufen',
    'account.profile_updated': 'Profil geändert',
    'linkedin.connected': 'LinkedIn verbunden',
    'linkedin.reconnected': 'LinkedIn neu verbunden',
    'linkedin.disconnected': 'LinkedIn getrennt',
    'linkedin.sync_requested': 'Synchronisation angestoßen',
    'linkedin.webhooks_subscribed': 'Webhooks abonniert',
    'linkedin.webhook_deleted': 'Webhook gelöscht',
    'lead.viewed': 'Lead mit Kontaktdaten angesehen',
    'leads.exported': 'Leads exportiert',
    'lead.erased': 'Personendaten gelöscht',
    'crm.settings_changed': 'CRM-Einstellungen geändert',
    'crm.campaign_map_changed': 'CRM-Kampagnenzuordnung geändert',
    'crm.released': 'Leads fürs CRM freigegeben',
    'crm.retried': 'CRM-Übertragung wiederholt',
    'dashboard.created': 'Dashboard angelegt',
    'dashboard.saved': 'Dashboard-Entwurf gespeichert',
    'dashboard.proposed': 'Dashboard-Vorschlag eingereicht',
    'dashboard.published': 'Dashboard veröffentlicht',
    'dashboard.rejected': 'Dashboard-Vorschlag abgelehnt',
    'dashboard.access_changed': 'Dashboard-Freigaben geändert',
    'dataset.saved': 'Datenquelle gespeichert',
    'dataset.deleted': 'Datenquelle gelöscht'
  },
  conflicts: {
    last_admin: 'Die letzte aktive Administratorin bzw. der letzte aktive Administrator kann diese Rolle nicht verlieren.',
    self: 'Das kannst du nicht bei dir selbst tun.',
    email_taken: 'Mit dieser E-Mail-Adresse gibt es schon einen Benutzer.',
    invite_open: 'Für diese Adresse gibt es schon eine offene Einladung.',
    system_role: 'Die Administrator-Rolle lässt sich nicht ändern.',
    role_in_use: 'Die Rolle ist noch Benutzern zugewiesen.',
    name_taken: 'Eine Rolle mit diesem Namen gibt es schon.',
    wildcard_reserved: 'Alle Berechtigungen hat nur die Administrator-Rolle.',
    connection_inactive: 'Die LinkedIn-Verbindung ist nicht aktiv. Bitte neu verbinden.',
    webhook_unreachable: 'LinkedIn kann diese Adresse nicht erreichen (nur öffentliche HTTPS-Adressen).',
    crm_not_configured: 'Auf diesem Server ist kein CRM eingerichtet.',
    unknown_attribute: 'Dieses Feld gibt es im CRM nicht (oder es lässt sich nicht befüllen). Lade die CRM-Felder neu, falls es gerade angelegt wurde.',
    not_failed: 'Nur fehlgeschlagene Übertragungen lassen sich wiederholen.',
    duplicate_campaign: 'Eine LinkedIn-Kampagne ist doppelt zugeordnet.',
    not_proposed: 'Nur Vorschläge lassen sich ablehnen.',
    bigquery_not_configured: 'BigQuery ist auf diesem Server nicht eingerichtet.',
    query_too_expensive: 'Diese Abfrage würde zu viele Daten lesen. Grenze den Zeitraum oder die Auswahl ein.',
    dataset_not_allowed: 'Dieses BigQuery-Dataset ist nicht freigegeben.',
    dataset_in_use: 'Die Datenquelle wird noch in Dashboards verwendet.',
    builtin_dataset: 'Eingebaute Datenquellen lassen sich nicht ändern.'
  },
  linkedin: {
    title: 'LinkedIn',
    section: 'LinkedIn',
    overview: 'Verbindung',
    campaigns: 'Kampagnen',
    leads: 'Leads',
    accounts: 'Werbekonten',
    campaignsCount: 'Kampagnen',
    leadsTotal: 'Leads gesamt',
    leadsWeek: 'Leads (7 Tage)',
    connections: 'Verbindungen',
    connect: 'Mit LinkedIn verbinden',
    reconnect: 'Neu verbinden',
    disconnect: 'Trennen',
    confirmDisconnect: 'Verbindung „{{label}}“ trennen? Bereits synchronisierte Daten bleiben erhalten.',
    noConnection: 'Noch keine LinkedIn-Verbindung. Verbinde ein LinkedIn-Konto mit Rollen für die Werbekonten und die Unternehmensseite.',
    connectHint: 'Du wirst zu LinkedIn weitergeleitet und bestätigst dort den Lesezugriff auf Werbekonten, Statistiken und Leads.',
    status: { active: 'Aktiv', expired: 'Abgelaufen', revoked: 'Widerrufen', error: 'Fehler' },
    connectedAt: 'Verbunden am',
    tokenValidUntil: 'Zugriff gültig bis',
    refreshValidUntil: 'Erneuerung möglich bis',
    noRefresh: 'Keine automatische Erneuerung – nach Ablauf neu verbinden.',
    lastFullSync: 'Letzte Vollsynchronisation',
    lastLeadSync: 'Letzte Lead-Synchronisation',
    syncFull: 'Alles synchronisieren',
    syncLeads: 'Leads synchronisieren',
    syncQueued: 'Synchronisation gestartet.',
    runs: 'Letzte Läufe',
    run: { full: 'Vollständig', leads: 'Leads', analytics: 'Statistiken', lead: 'Webhook-Lead' },
    runStatus: { running: 'läuft', ok: 'ok', failed: 'fehlgeschlagen' },
    noRuns: 'Noch keine Läufe.',
    connectResult: {
      ok: 'LinkedIn ist verbunden. Die erste Synchronisation läuft.',
      connected_no_accounts: 'Verbunden, aber das Konto sieht keine Werbekonten. Prüfe die Rollen in LinkedIn Campaign Manager.',
      cancelled: 'Die Verbindung wurde bei LinkedIn abgebrochen.',
      invalid: 'Die Verbindung konnte nicht abgeschlossen werden. Bitte erneut versuchen.',
      exchange_failed: 'LinkedIn hat den Zugriff nicht bestätigt. Bitte erneut versuchen.'
    },
    webhooks: 'Echtzeit-Benachrichtigungen',
    webhookUrl: 'Webhook-URL',
    webhookUnreachable: 'Diese Adresse ist für LinkedIn nicht erreichbar (nur öffentliche HTTPS-Adressen). Webhooks funktionieren erst auf dem Server.',
    webhookSteps: 'Einmalig: Diese URL im LinkedIn Developer Portal unter „Webhooks“ eintragen und validieren lassen. Danach hier abonnieren.',
    subscribe: 'Alle Konten abonnieren',
    subscribed: '{{created}} neu, {{existing}} bestehend, {{failed}} fehlgeschlagen.',
    noSubscriptions: 'Keine Abonnements.',
    account: 'Konto',
    role: 'Rolle',
    currency: 'Währung',
    campaign: 'Kampagne',
    group: 'Kampagnengruppe',
    objective: 'Ziel',
    impressions: 'Impressionen',
    clicks: 'Klicks',
    cost: 'Kosten',
    period: 'Laufzeit',
    allAccounts: 'Alle Konten',
    noCampaigns: 'Noch keine Kampagnen synchronisiert.'
  },
  leads: {
    title: 'Leads',
    search: 'Suchen (Name, Firma{{email}})',
    searchEmail: ', E-Mail',
    includeTest: 'Testleads anzeigen',
    submitted: 'Eingegangen',
    name: 'Name',
    company: 'Firma',
    email: 'E-Mail',
    campaign: 'Kampagne',
    form: 'Formular',
    none: 'Keine Leads gefunden.',
    test: 'Test',
    withdrawn: 'Zurückgezogen',
    erased: 'Gelöscht (DSGVO)',
    masked: 'Kontaktdaten sind ausgeblendet – dafür fehlt dir die Berechtigung.',
    answers: 'Antworten',
    consents: 'Einwilligungen',
    consentGiven: 'erteilt',
    consentDenied: 'nicht erteilt',
    details: 'Details',
    jobTitle: 'Position',
    country: 'Land',
    phone: 'Telefon',
    firstSeen: 'In Holymoly seit',
    exportCsv: 'CSV exportieren',
    exportHint: 'Exportiert die gefilterten Leads (höchstens 20 000). Jeder Export wird protokolliert.',
    erase: 'Personendaten löschen',
    eraseTitle: 'Personendaten löschen (DSGVO)',
    eraseText: 'Name, Kontaktdaten, Antworten und die LinkedIn-Rohdaten werden unwiderruflich entfernt. Datum, Formular und Kampagne bleiben für Statistiken erhalten. Eine spätere LinkedIn-Synchronisation füllt die Daten nicht wieder auf.',
    eraseSameEmail: 'Alle Leads mit dieser E-Mail-Adresse löschen',
    eraseConfirm: 'Endgültig löschen',
    erasedDone: '{{count}} Lead(s) anonymisiert.',
    eraseCrmNote: 'Im CRM gibt es {{count}} zugehörige Lead(s). Die musst du dort separat löschen:'
  },
  crm: {
    title: 'CRM-Synchronisation',
    section: 'CRM',
    queue: 'Übertragungen',
    mapping: 'Feldzuordnung',
    notConfigured: 'Auf diesem Server ist kein CRM eingerichtet (CRM_INSTANCE_URI fehlt).',
    instance: 'CRM-Instanz',
    mode: 'Synchronisation',
    modeLive: 'Live – Leads werden angelegt',
    modeDryRun: 'Probelauf – nichts wird ans CRM gesendet',
    syncOn: 'Synchronisation an',
    on: 'An',
    off: 'Aus',
    syncOff: 'Synchronisation aus',
    syncOffHint: 'Neue Leads werden zurückgehalten, bis du die Synchronisation einschaltest und sie freigibst.',
    enable: 'Einschalten',
    disable: 'Ausschalten',
    confirmEnable: 'Synchronisation einschalten? Neue Leads gehen dann automatisch ins CRM (höchstens {{cap}}). Bereits zurückgehaltene Leads bleiben zurückgehalten, bis du sie freigibst.',
    confirmEnableUnlimited: 'Synchronisation einschalten? Neue Leads gehen dann automatisch und ohne Obergrenze ins CRM. Bereits zurückgehaltene Leads bleiben zurückgehalten, bis du sie freigibst.',
    cap: 'Obergrenze',
    capUsage: '{{sent}} von {{cap}} gesendet',
    capUnlimited: 'Keine Obergrenze ({{sent}} gesendet)',
    metadata: 'CRM-Felder',
    metadataSyncedAt: 'Stand: {{date}}',
    metadataNever: 'Noch nicht geladen.',
    refreshMetadata: 'Neu laden',
    metadataQueued: 'Felder werden neu geladen – in ein paar Sekunden aktualisieren.',
    held: 'Zurückgehalten',
    releaseAll: 'Alle freigeben ({{count}})',
    releaseSelected: 'Auswahl freigeben ({{count}})',
    confirmReleaseAll: '{{count}} zurückgehaltene Leads freigeben? Sie werden ins CRM übertragen, soweit die Obergrenze es zulässt.',
    released: '{{count}} Leads freigegeben.',
    retry: 'Wiederholen',
    retried: 'Übertragung wird wiederholt.',
    release: 'Freigeben',
    openInCrm: 'Im CRM öffnen',
    noRows: 'Keine Übertragungen.',
    attempts: 'Versuche',
    lead: 'Lead',
    createdAt: 'Eingereiht',
    sentAt: 'Gesendet',
    error: 'Fehler',
    requestId: 'Request-ID',
    filterAll: 'Alle',
    statuses: {
      pending: 'Wartet',
      sending: 'Wird gesendet',
      sent: 'Gesendet',
      failed: 'Fehlgeschlagen',
      held: 'Zurückgehalten',
      skipped: 'Übersprungen',
      simulated: 'Probelauf'
    },
    holdReasons: {
      disabled: 'Synchronisation aus',
      cap: 'Obergrenze erreicht',
      consent_missing: 'Einwilligung fehlt',
      match_ambiguous: 'Kontakt nicht eindeutig',
      mapping_incomplete: 'Weder E-Mail noch Nachname'
    },
    skipReasons: { test_lead: 'Testlead', erased: 'Personendaten gelöscht' },
    match: {
      linked: 'Mit Kontakt verknüpft',
      linked_most_recent: 'Mit neuestem Kontakt verknüpft',
      ambiguous: 'Mehrere Kontakte – nicht verknüpft, bitte prüfen',
      none: 'Kein Kontakt gefunden'
    },
    leadCard: 'CRM',
    notQueued: 'Dieser Lead war noch nie für das CRM vorgesehen.',
    fieldMap: 'Felder',
    fieldMapHint: 'Woher jeder Wert kommt und in welches Lead-Feld im CRM er geschrieben wird.',
    source: 'Quelle',
    attribute: 'CRM-Feld',
    type: 'Typ',
    types: { string: 'Text', number: 'Zahl', boolean: 'Ja/Nein', picklist: 'Auswahl', date: 'Datum', json: 'JSON-Feld' },
    jsonKey: 'Schlüssel',
    jsonHint: 'Typ „JSON-Feld“: Alle Zeilen mit demselben CRM-Feld werden zu einem JSON-Objekt zusammengefasst ({"Schlüssel": "Wert"}), z. B. für utm_content.',
    addRow: 'Zeile hinzufügen',
    removeRow: 'Zeile entfernen',
    sourceKinds: { field: 'Lead-Feld', answer: 'Formular-Antwort', meta: 'Metadaten', constant: 'Fester Wert', template: 'Vorlage' },
    leadFields: { firstName: 'Vorname', lastName: 'Nachname', email: 'E-Mail', phone: 'Telefon', company: 'Firma', jobTitle: 'Position', country: 'Land', city: 'Ort' },
    metaKeys: { linkedinLeadId: 'LinkedIn-Lead-ID', form: 'Formularname', campaign: 'Kampagnenname', submittedAt: 'Eingangszeitpunkt' },
    sourceValue: 'Wert',
    valueMap: 'Antwort → Auswahlwert',
    valueMapHint: 'Eine Zeile pro Antwort: Antworttext = Zahl (Optionswert im CRM).',
    fixed: 'Feste Werte',
    subjectTemplate: 'Thema',
    subjectHint: 'Platzhalter: {{placeholders}}',
    leadSource: 'Lead-Quelle',
    linkedinIdAttribute: 'Feld für die LinkedIn-Lead-ID',
    linkedinIdHint: 'Damit lässt sich jeder CRM-Lead seinem LinkedIn-Lead zuordnen. Leer = nicht speichern.',
    none: '– keins –',
    matching: 'Kontaktabgleich',
    contactsActiveOnly: 'Nur aktive Kontakte berücksichtigen',
    multipleMatches: 'Wenn mehrere Kontakte die E-Mail-Adresse haben',
    multiple: { flag: 'Nicht verknüpfen und markieren', link_most_recent: 'Zuletzt geänderten Kontakt verknüpfen', skip_links: 'Nicht verknüpfen' },
    consents: 'Pflicht-Einwilligungen',
    consentsHint: 'LinkedIn-Einwilligungs-IDs, kommagetrennt. Leads ohne diese Einwilligungen werden zurückgehalten.',
    includeTestLeads: 'Testleads auch übertragen',
    campaigns: 'Kampagnen',
    campaignsHint: 'LinkedIn-Kampagne → Kampagne im CRM (wird am Lead hinterlegt).',
    noCrmCampaign: '– keine –',
    save: 'Speichern',
    saved: 'Gespeichert.',
    preview: 'Vorschau',
    previewHint: 'Baut das CRM-Lead für einen echten Lead mit der Zuordnung oben, ohne etwas zu senden. Der Kontaktabgleich ist nicht Teil der Vorschau.',
    previewLead: 'Lead-ID',
    previewRun: 'Vorschau erzeugen',
    previewSkipped: 'Ohne Wert: {{attributes}}',
    unsaved: 'Ungespeicherte Änderungen'
  },
  dashboards: {
    title: 'Dashboards',
    section: 'Dashboards',
    new: 'Neues Dashboard',
    name: 'Name',
    template: 'Vorlage',
    templates: { empty: 'Leer', linkedin_performance: 'LinkedIn-Performance', lead_funnel: 'Leads und CRM' },
    visibility: 'Sichtbarkeit',
    visibilityAll: 'Alle mit Dashboard-Zugriff',
    visibilityRestricted: 'Nur freigegebene Personen und Rollen',
    create: 'Anlegen',
    none: 'Noch keine Dashboards.',
    owner: 'Verantwortlich',
    updated: 'Geändert',
    edit: 'Bearbeiten',
    statuses: { draft: 'Entwurf', published: 'Veröffentlicht', proposed: 'Vorschlag', rejected: 'Abgelehnt' },
    proposals: 'Vorschläge: {{count}}',
    notPublished: 'Noch nicht veröffentlicht',
    viewingVersion: 'Du siehst Version {{version}} ({{status}}).',
    showPublished: 'Veröffentlichte Version anzeigen',
    noData: 'Keine Daten im gewählten Zeitraum.',
    loadError: 'Konnte nicht geladen werden.',
    vsPrevious: 'ggü. Vorperiode',
    period: 'Zeitraum',
    from: 'Von',
    to: 'Bis',
    custom: 'Benutzerdefiniert',
    all: 'Alle',
    selected: '{{count}} ausgewählt',
    presets: {
      today: 'Heute',
      yesterday: 'Gestern',
      last_7_days: 'Letzte 7 Tage',
      last_14_days: 'Letzte 14 Tage',
      last_30_days: 'Letzte 30 Tage',
      last_90_days: 'Letzte 90 Tage',
      last_365_days: 'Letzte 365 Tage',
      this_week: 'Diese Woche',
      last_week: 'Letzte Woche',
      this_month: 'Dieser Monat',
      last_month: 'Letzter Monat',
      this_quarter: 'Dieses Quartal',
      last_quarter: 'Letztes Quartal',
      this_year: 'Dieses Jahr',
      last_year: 'Letztes Jahr'
    },
    rowCount: '{{count}} Zeilen',
    previousPage: 'Zurück',
    nextPage: 'Weiter'
  },
  builder: {
    drag: 'Ziehen zum Verschieben',
    back: 'Zurück zum Dashboard',
    basedOn: 'Bearbeitet auf Basis von Version {{version}}',
    visual: 'Visuell',
    code: 'Code',
    versions: 'Versionen',
    sharing: 'Freigaben',
    notePlaceholder: 'Notiz zur Änderung (optional)',
    saveDraft: 'Entwurf speichern',
    publish: 'Veröffentlichen',
    savedDraft: 'Als Version {{version}} gespeichert (Entwurf).',
    published: 'Veröffentlicht.',
    issues: 'Probleme: {{count}} – so lässt sich nicht speichern.',
    add: 'Hinzufügen:',
    widget: 'Element',
    dashboard: 'Dashboard',
    dashboardSettings: 'Dashboard-Einstellungen',
    widgetTitle: 'Überschrift',
    type: 'Darstellung',
    types: { kpi: 'Kennzahl', timeseries: 'Zeitverlauf', bar: 'Balken', donut: 'Ring', table: 'Tabelle', markdown: 'Text' },
    content: 'Text',
    markdownHint: 'Markdown: # Überschrift, **fett**, *kursiv*, - Liste, [Link](https://…)',
    removeWidget: 'Element entfernen',
    data: 'Daten',
    dataset: 'Datenquelle',
    measures: 'Kennzahlen',
    dimensions: 'Aufteilen nach',
    none: '– keine –',
    grain: 'Zeiteinheit',
    grains: { day: 'Tag', week: 'Woche', month: 'Monat', quarter: 'Quartal', year: 'Jahr' },
    appliesFilters: 'Filter anwenden',
    limit: 'Höchstens Zeilen',
    display: 'Anzeige',
    compare: 'Mit Vorperiode vergleichen',
    chart: 'Diagrammart',
    charts: { line: 'Linie', area: 'Fläche', bar: 'Säulen' },
    stacked: 'Gestapelt',
    horizontal: 'Horizontal',
    pageSize: 'Zeilen pro Seite',
    dashboardTitle: 'Titel',
    description: 'Beschreibung',
    filters: 'Filter',
    removeFilter: 'Filter entfernen',
    filterLabel: 'Beschriftung',
    multi: 'Mehrfachauswahl',
    addDateFilter: 'Zeitraum',
    addSelectFilter: 'Auswahlfilter',
    viaToken: 'Über ein API-Token erstellt (z. B. von einem KI-Agenten)',
    compareWith: 'Version {{a}} → {{b}}',
    loadIntoEditor: 'In den Editor laden',
    reject: 'Ablehnen',
    rejectedDone: 'Vorschlag abgelehnt.',
    accept: 'Annehmen und veröffentlichen',
    restore: 'Wiederherstellen',
    noChanges: 'Keine Unterschiede.',
    sharesHint: 'Zusätzlich freigeben für:',
    access: 'Recht',
    viewer: 'Ansehen',
    editor: 'Bearbeiten',
    removeShare: 'Freigabe entfernen',
    addShare: 'Person oder Rolle hinzufügen …'
  },
  datasets: {
    title: 'Datenquellen',
    new: 'Neue Datenquelle',
    editTitle: 'Datenquelle {{id}} bearbeiten',
    notConfigured: 'BigQuery ist auf diesem Server nicht eingerichtet (BIGQUERY_PROJECT fehlt). Die eingebauten Holymoly-Datenquellen funktionieren trotzdem.',
    project: 'GCP-Projekt',
    location: 'Standort',
    maxBytes: 'Höchstens pro Abfrage',
    credentials: 'Anmeldung',
    credentialKinds: { workload_identity: 'Workload Identity Federation (ohne Schlüssel)', service_account_key: 'Dienstkonto-Schlüssel', default: 'Standard-Anmeldedaten', none: '–' },
    allowed: 'Freigegebene BigQuery-Datasets',
    dataset: 'Datenquelle',
    source: 'Quelle',
    usedBy: 'Verwendet in',
    builtin: 'eingebaut',
    disabled: 'deaktiviert',
    invalid: 'fehlerhaft',
    enabled: 'Aktiv',
    table: 'BigQuery-Tabelle',
    loadColumns: 'Spalten laden',
    editorHint: 'Kennzahlen sind Summen, Anzahlen oder Quotienten aus Spalten – kein SQL. Zeitfilter brauchen eine Spalte vom Typ DATE.',
    confirmDelete: 'Datenquelle {{id}} löschen?'
  },
  languages: {
    de: 'Deutsch',
    en: 'English'
  },
  overview: {
    title: 'Übersicht',
    systemStatus: 'Systemstatus',
    statusOk: 'Alle Dienste erreichbar',
    statusDegraded: 'Eingeschränkt',
    database: 'Datenbank',
    redis: 'Warteschlange (Redis)',
    version: 'Version',
    reachable: 'erreichbar',
    unreachable: 'nicht erreichbar',
    latency: '{{ms}} ms'
  },
  notFound: {
    title: 'Seite nicht gefunden',
    description: 'Diese Seite gibt es nicht (mehr).',
    back: 'Zur Übersicht'
  },
  mail: {
    greeting: 'Hallo,',
    signature: 'Viele Grüße\nHolymoly · Bechtle PLM Marketing',
    invite: {
      subject: 'Einladung zu Holymoly',
      body: '{{inviter}} hat dich zu Holymoly eingeladen, der Marketing-Plattform der Bechtle PLM.\n\nÜber diesen Link legst du dein Passwort fest (gültig bis {{expires}}):\n{{url}}\n\nWenn du mit dieser Einladung nichts anfangen kannst, ignoriere diese E-Mail einfach.'
    },
    crmFailures: {
      subject: 'Holymoly: {{count}} Lead(s) konnten nicht ins CRM übertragen werden',
      body: '{{count}} Lead(s) wurden vom CRM abgelehnt oder konnten nach mehreren Versuchen nicht übertragen werden:\n\n{{lines}}\n\nDetails und „Wiederholen“:\n{{url}}\n\n(Aus Datenschutzgründen stehen hier keine Kontaktdaten.)'
    },
    resetPassword: {
      subject: 'Holymoly: Passwort zurücksetzen',
      body: 'Für dein Holymoly-Konto wurde ein neues Passwort angefordert.\n\nÜber diesen Link legst du es fest (eine Stunde gültig):\n{{url}}\n\nWarst du das nicht? Dann ignoriere diese E-Mail – dein Passwort bleibt unverändert.'
    }
  },
  /** Stable API error codes (packages/contract/src/base.ts) → user-facing text. */
  errors: {
    UNAUTHORIZED: 'Bitte melde dich an.',
    FORBIDDEN: 'Dafür fehlt dir die Berechtigung.',
    NOT_FOUND: 'Nicht gefunden.',
    CONFLICT: 'Das wurde inzwischen geändert. Bitte neu laden.',
    RATE_LIMITED: 'Zu viele Anfragen. Bitte kurz warten.',
    TWO_FACTOR_REQUIRED: 'Richte zuerst die Zwei-Faktor-Authentifizierung ein.',
    INTERNAL_SERVER_ERROR: 'Auf dem Server ist ein Fehler aufgetreten. Bitte versuche es später noch einmal.',
    BAD_REQUEST: 'Die Eingabe ist ungültig.',
    NETWORK: 'Keine Verbindung zum Server.'
  }
} as const;
